import {Avatar, Box, Button, Card, IconButton, Stack, Typography} from "@mui/joy";
import {useSessionCookie} from "../../hooks/useSessionCookie";
import React, {ReactNode, useEffect, useState} from "react";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {ArrowLeft, BookOpenText, ClipboardCheckIcon, Clock, Dumbbell, Ear, Edit3, Mic, Timer} from "lucide-react";
import {SectionName} from "../../models/section";
import {useAsync} from "../../hooks/useAsyncResult";
import {getExamResourceImg} from "../../services/api/exam";
import {User} from "../../models/user";
import LoadingPageComponent from "../Loading/LoadingPageComponent";
import IconBoxComponent from "../IconBoxComponent";
import {TestMode} from "../../models/test";
import TestRoomComponent from "./TestRoom/TestRoomComponent";
import TestRoomContext from "../../context/TestRoomContext";
import {useTranslation} from "react-i18next";
import {useNotification} from "../../hooks/useNotification";
import EnableNotificationsDialogComponent from "./EnableNotificationsDialogComponent";
import {supportsFCM} from "../../utils/messaging";
import {usePWA} from "../../hooks/usePWA";
import {usePlatform} from "../../hooks/usePlatform";
import AndroidInstallPromptDialogComponent from "../Config/AndroidInstallPromptDialogComponent";
import IOSInstallPromptDialogComponent from "../Config/IOSInstallPromptDialogComponent";
import LoadingError from "../Error/LoadingError";

const PracticeTestRoom = () => {
    const [testRoomVisible, setTestRoomVisible] = useState(false)
    const {notificationsEnabled} = useNotification()
    const [enableNotificationsDialogVisible, setEnableNotificationsDialogVisible] = useState(false)
    const [testMode, setTestMode] = useState<TestMode>()
    const {sectionName} = useParams()
    const pwa = usePWA()
    const platform = usePlatform()
    const examResImgAsyncResult = useAsync(getExamResourceImg, true)
    const {userSession} = useSessionCookie()
    const {t} = useTranslation()
    const [searchParams] = useSearchParams()
    const user: User | undefined = userSession?.['currentUser']

    const handleStartTest = (mode: TestMode) => {
        setTestRoomVisible(true)
        setTestMode(mode)
    }

    const handleCloseTest = () => {
        setTestRoomVisible(false)
        setTestMode(undefined)
    }

    useEffect(() => {
        if (searchParams.get("testId"))
            setTestRoomVisible(true)
    }, [searchParams])

    useEffect(() => {
        examResImgAsyncResult.execute(user?.preferredExam)
        setEnableNotificationsDialogVisible(!notificationsEnabled)
    }, [])

    return <Stack width={"100%"} boxSizing={"border-box"} padding={4}>

        {examResImgAsyncResult.loading ?
            <LoadingPageComponent label={`${sectionName} Test`}/> :

            <Stack alignSelf={"center"} gap={4} alignItems={"center"}>

                <Stack alignItems={"center"} gap={1}>
                    <Stack direction={"row"} gap={1} alignItems={"center"}>
                        <Avatar size={"lg"} sx={{'--Avatar-size':"5rem"}}
                                src={examResImgAsyncResult.payload}/>

                        <Typography level={"h1"} textTransform={"capitalize"}>
                            {sectionName}
                        </Typography>

                    </Stack>
                    <Typography fontSize={24} textAlign={{xs:"center"}}>
                        {t("practiceModeSelectionTitle")}
                    </Typography>
                </Stack>

                <Stack direction={{xs: "column"}} maxWidth={"40rem"} gap={4}>
                    <Card sx={{padding: 3.5}}>
                        <Stack gap={2} paddingY={2}>
                            <IconBoxComponent color={"primary"} style={{alignSelf: "center", padding: 2}}>
                                <ClipboardCheckIcon style={{fontSize: 57, color: "inherit"}}/>
                            </IconBoxComponent>

                            <Stack>
                                <Typography level={"h3"} textAlign={"center"}>
                                    {t("mockTestSelectionTitle")}
                                </Typography>

                                <Typography textAlign={"center"}>
                                    {t("mockTestSelectionDescription")}
                                </Typography>

                            </Stack>


                            <Card variant={"soft"}>
                                <Stack direction={"row"} gap={2} alignItems={"center"}>
                                    <IconBoxComponent color={"primary"}>
                                        <Timer style={{fontSize: 30, color: "inherit"}}/>
                                    </IconBoxComponent>

                                    <Stack>
                                        <Typography level={"title-lg"}>
                                            {t("strictTimeEnforcementTitle")}
                                        </Typography>

                                        <Typography level={"body-xs"}>
                                            {t("strictTimeEnforcementDescription")}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Card>


                            <Button onClick={() => handleStartTest("mock")}
                                    variant={"soft"} size={"lg"}>
                                {t("startMockTestActionButtonLabel")}
                            </Button>
                        </Stack>
                    </Card>


                    <Card sx={{padding: 3.5}}>
                        <Stack gap={2} paddingY={2}>
                            <IconBoxComponent color={"secondary"} style={{alignSelf: "center", padding: 2}}>
                                <Dumbbell style={{fontSize: 57, color: "inherit"}}/>
                            </IconBoxComponent>

                            <Stack>
                                <Typography level={"h3"} textAlign={"center"}>
                                    {t("exerciseModeSelectionTitle")}
                                </Typography>

                                <Typography textAlign={"center"}>
                                    {t("exerciseModeSelectionDescription")}
                                </Typography>

                            </Stack>


                            <Card variant={"soft"}>
                                <Stack direction={"row"} alignItems={"center"} gap={2}>
                                    <IconBoxComponent color={"secondary"}>
                                        <Clock style={{fontSize: 30, color: "inherit"}}/>
                                    </IconBoxComponent>
                                    <Stack>
                                        <Typography level={"title-lg"}>
                                            {t("noTimeEnforcementTitle")}
                                        </Typography>

                                        <Typography level={"body-xs"}>
                                            {t("noTimeEnforcementDescription")}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Card>


                            <Button onClick={() => handleStartTest("practice")}
                                    variant={"soft"} size={"lg"} color={"secondary"}>
                                {t("startExerciseModeActionButtonLabel")}
                            </Button>

                        </Stack>
                    </Card>
                </Stack>

                {testRoomVisible &&
                    <TestRoomContext>
                        <TestRoomComponent mode={testMode || searchParams.get("mode") as TestMode} onClose={handleCloseTest}
                                           section={sectionName as SectionName}
                                           open={testRoomVisible}/>
                    </TestRoomContext>}

                {typeof Notification !== "undefined" && <EnableNotificationsDialogComponent open={enableNotificationsDialogVisible}
                                                     onClose={() => setEnableNotificationsDialogVisible(false)}/>}


            </Stack>}

        {(platform.isMobile && !pwa.isPWA) ? platform.isAndroid ? <AndroidInstallPromptDialogComponent/> :
                <IOSInstallPromptDialogComponent/> :
            <></>}
    </Stack>
}

export default PracticeTestRoom